import type { PlanBillingInterval, UpgradePlanName } from './plan-pricing';
import { PLAN_LIST_PRICING_USD } from './plan-pricing';

const BASIS_POINTS = 10_000;

// Plans as the billing page and the plans modal present them. Prices are USD list prices;
// other currencies come from Dodo at runtime.
export type PlanPrice = {
  compareAt: number | null;
  current: number;
  annualTotal?: number;
  currency?: string;
};

export type PlanFeatureItem = string | { text: string; tooltip: string };

export interface PlanFeatureGroup {
  label: string;
  items: PlanFeatureItem[];
}

// Shown on the Monthly/Annually switch on the billing page and in the plans modal.
export const ANNUAL_BILLING_SAVINGS_PERCENT = 15;

export interface OfferPlanPrice {
  /** The base price, struck through on the cards. */
  struck: number;
  /** Price per month with the offer, in whole currency units. */
  monthly: number;
  /** Yearly charge for annual billing. */
  annualTotal?: number;
}

const percentOff = (amount: number, percent: number) => Math.round((amount * (100 - percent)) / 100);

// The offer takes its percent off the base price, and annual billing a further 15%, each rounded
// to a whole amount. A `discountPercent` of 0 gives the regular price shown without an offer.
// Checkout sizes the Dodo discount to land on these numbers, so every surface that shows an
// offer price must use this function.
export function getOfferPlanPrice(
  price: Pick<PlanPrice, 'compareAt' | 'current'>,
  interval: PlanBillingInterval,
  discountPercent: number,
): OfferPlanPrice {
  const struck = price.compareAt ?? price.current;
  const monthlyOffer = percentOff(struck, discountPercent);
  if (interval === 'monthly') return { struck, monthly: monthlyOffer };

  const annualMonthly = percentOff(monthlyOffer, ANNUAL_BILLING_SAVINGS_PERCENT);
  return { struck, monthly: annualMonthly, annualTotal: annualMonthly * 12 };
}

// Dodo discounts are a percentage, in basis points, of the product being bought. Each plan and
// interval needs its own percentage to land on `getOfferPlanPrice`; basis points can leave the
// charged total a cent off.
export function getOfferDiscountBasisPoints(
  planName: UpgradePlanName,
  interval: PlanBillingInterval,
  discountPercent: number,
): number {
  const list = PLAN_LIST_PRICING_USD[planName][interval];
  const offerPrice = getOfferPlanPrice(list, interval, discountPercent);
  const [full, target] =
    interval === 'annual'
      ? [list.annualTotal ?? list.current * 12, offerPrice.annualTotal ?? offerPrice.monthly * 12]
      : [list.current, offerPrice.monthly];
  return Math.max(0, Math.round((1 - target / full) * BASIS_POINTS));
}

export interface PlanDefinition {
  name: UpgradePlanName;
  description: string;
  pricing: Record<PlanBillingInterval, PlanPrice>;
  billingNote?: string;
  defaultFeatures?: string[];
  featureGroups: PlanFeatureGroup[];
  // Short list for the plans modal; the billing page shows the full feature groups.
  keyFeatures: string[];
  featured?: boolean;
  recommended?: boolean;
}

export const PLAN_DEFINITIONS: PlanDefinition[] = [
  {
    name: 'Starter',
    description: 'For solo professionals and freelancers',
    pricing: PLAN_LIST_PRICING_USD.Starter,
    defaultFeatures: ['1 Seat included', '2,000 AI credits/mo', '10 Content sources'],
    keyFeatures: ['Document allowance', 'Template access', 'Sharing options', 'Support level'],
    featureGroups: [
      {
        label: 'Included features:',
        items: ['All document types', 'Limited integrations:'],
      },
      {
        label: 'Document output:',
        items: ['Export to PDF', 'E-signatures'],
      },
    ],
    featured: false,
    recommended: false,
  },
  {
    name: 'Professional',
    description: 'For small teams (2–5 people)',
    pricing: PLAN_LIST_PRICING_USD.Professional,
    defaultFeatures: [
      '3 Seats total · Additional seats available',
      '6,000 AI credits/workspace',
      '30 Content sources',
    ],
    keyFeatures: ['Document allowance', 'Branding controls', 'Export formats', 'Workspace options'],
    featureGroups: [
      {
        label: 'Everything in Starter, plus:',
        items: [
          {
            text: 'Remove proposal.biz branding',
            tooltip:
              'By default, public documents display a "Made with proposal.biz" badge. Upgrade to the Professional plan to remove this branding.',
          },
          'Custom domain',
          'All integrations:',
          'Chat and email support (24h response)',
        ],
      },
      {
        label: 'Document output:',
        items: ['Export to PDF & PPT', 'Send & track opens'],
      },
    ],
    featured: true,
    recommended: true,
  },
  {
    name: 'Business',
    description: 'For growing teams (5–20 people)',
    pricing: PLAN_LIST_PRICING_USD.Business,
    defaultFeatures: [
      '10 Seats total · Additional seats available',
      '10,000 AI credits/workspace',
      '100 Content sources',
    ],
    keyFeatures: ['Team allowance', 'Email options', 'Administration', 'Support level'],
    featureGroups: [
      {
        label: 'Everything in Professional, plus:',
        items: ['Advanced Analytics', 'Priority support — 4h response', 'Bring your own API key'],
      },
    ],
    featured: false,
    recommended: false,
  },
];

export type PlanPricingOverrides = Partial<
  Record<UpgradePlanName, Partial<Record<PlanBillingInterval, PlanPrice | null>>>
>;

const inUsd = (price: PlanPrice): PlanPrice => ({ ...price, currency: 'USD' });

// Plans priced in another currency. A price Dodo couldn't give keeps the USD list price.
export function localizePlans(overrides?: PlanPricingOverrides | null): PlanDefinition[] {
  return PLAN_DEFINITIONS.map((plan) => ({
    ...plan,
    pricing: {
      monthly: overrides?.[plan.name]?.monthly ?? inUsd(plan.pricing.monthly),
      annual: overrides?.[plan.name]?.annual ?? inUsd(plan.pricing.annual),
    },
  }));
}
